import { Link } from "react-router-dom";
import {
  Heart, Sparkles, Feather, Compass, Users, Wand2, ArrowRight, CheckCircle2,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import site from "@/data/site";

const iconMap = {
  heart: Heart, sparkles: Sparkles, feather: Feather, compass: Compass, users: Users, wand: Wand2,
};

function PageHeader() {
  return (
    <section className="relative bg-bloom overflow-hidden">
      <div className="container-luxe relative py-20 lg:py-28 text-center">
        <Reveal>
          <span className="eyebrow">Services</span>
          <h1 className="display-xl font-display mt-4">
            Heartfelt Transportation
            <span className="block font-script italic text-rose-ink leading-[1.05] mt-1">Beautifully Done.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-ink max-w-2xl mx-auto leading-relaxed">
            Personalized arrangements that reflect the life and legacy of your loved one, handled with grace and quiet luxury.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ServiceGrid() {
  return (
    <section className="section bg-ivory">
      <div className="container-luxe">
        <Reveal stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {site.services.map((s) => {
            const Icon = iconMap[s.icon] || Heart;
            return (
              <article
                key={s.key}
                data-testid={`service-${s.key}`}
                className="group bg-white rounded-2xl p-8 border border-line transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-25px_rgba(201,122,136,0.45)] hover:border-rose"
              >
                <div className="w-14 h-14 rounded-full bg-blush grid place-items-center group-hover:bg-rose transition-colors">
                  <Icon className="w-6 h-6 text-rose-ink group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl mt-6 leading-tight">{s.title}</h3>
                <p className="mt-3 text-muted-ink leading-relaxed">{s.blurb}</p>
                <div className="mt-6 pt-5 border-t border-line">
                  <Link to="/contact" className="text-rose-ink font-medium inline-flex items-center gap-2 group/cta">
                    Inquire <ArrowRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-1" />
                  </Link>
                </div>
              </article>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "01", t: "Connect", b: "A simple call or email starts the conversation." },
    { n: "02", t: "Listen", b: "We hear your story, your loved one, your wishes." },
    { n: "03", t: "Craft", b: "We tailor florals, attire, music cues and pacing." },
    { n: "04", t: "Honor", b: "On the day, every detail unfolds with quiet grace." },
  ];
  return (
    <section className="section bg-soft-blush relative overflow-hidden">
      <div className="container-luxe">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">How it works</span>
          <h2 className="display-lg font-display mt-3">A gentle, guided process.</h2>
        </Reveal>
        <Reveal stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="relative bg-white rounded-2xl p-7 border border-line hover:-translate-y-1 transition-transform duration-500">
              <p className="font-display text-5xl text-rose-soft leading-none">{s.n}</p>
              <h3 className="font-display text-2xl mt-4">{s.t}</h3>
              <p className="text-sm text-muted-ink mt-2 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function Inclusions() {
  const items = [
    "Signature pink hearse",
    "Drivers in coordinated pink attire",
    "Floral integration & styling",
    "Custom music cues",
    "Memorial procession routing",
    "Family-focused timing & care",
    "Greater Houston area coverage",
    "Personalized requests welcomed",
  ];
  return (
    <section className="section bg-ivory">
      <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <span className="eyebrow">What's included</span>
          <h2 className="display-lg font-display mt-3">Every farewell, beautifully handled.</h2>
          <p className="mt-5 text-muted-ink leading-relaxed">
            We don't sell packages. We craft moments. Here's what families can expect from any Eternal Care arrangement.
          </p>
        </Reveal>
        <Reveal stagger className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
          {items.map((x) => (
            <div key={x} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-line hover:border-rose transition-colors">
              <CheckCircle2 className="w-5 h-5 text-rose-ink shrink-0" strokeWidth={1.5} />
              <span>{x}</span>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative bg-charcoal text-white overflow-hidden">
      <div className="absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-rose/30 blur-3xl" aria-hidden="true" />
      <div className="container-luxe relative py-20 text-center">
        <Reveal>
          <p className="font-script text-rose-soft text-3xl">Let's craft something beautiful</p>
          <h2 className="display-lg font-display mt-3">Tell us about your loved one.</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/contact" className="btn-pill btn-primary" data-testid="services-cta-contact">
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={site.emailHref} className="btn-pill btn-ivory" data-testid="services-cta-email">
              Email Us
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <>
      <Seo
        title="Services | Pink Hearse, Memorial & Family Escort | Eternal Care"
        description="Funeral transportation, celebration of life, memorial processions, family escort, and personalized pink hearse experiences in Greater Houston, TX."
        path="/services"
      />
      <PageHeader />
      <ServiceGrid />
      <Process />
      <Inclusions />
      <CtaBand />
    </>
  );
}
